import { useMutation } from '@apollo/client';
import {
	Airplane, LoaderBars, useUrlParams, WarningSignOutline, CheckIcon,
	Button,
	ButtonAppearance,
	ButtonColor,
} from '@aleph/shared-components';
import { useSuperGridValidate } from '@aleph/supergrid';
import {
	MarkNotificationsAsReadDocument,
	NotificationType,
	RedditNotificationsDocument,
} from 'wb-reddit/graphql/generated';
import {
	getUrlParamsAndValidationsFromNotification,
	RedditNotification,
} from 'wb-reddit/app/routes/superGrid/actionRow/notifications/util';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const NotificationItem = ({ notification, onClose }: { notification: RedditNotification, onClose: () => void }) => {
	const { context: { setValidations } } = useSuperGridValidate();
	const navigate = useNavigate();
	const { setUrlParams, params } = useUrlParams({ navigate, useParams });
	const [markNotificationsAsRead, { loading }] = useMutation(MarkNotificationsAsReadDocument, {
		errorPolicy: 'all',
		update:      (cache) => {
			const unreadNotifsQuery = {
				query: 				  RedditNotificationsDocument,
				errorPolicy: 'all',
				variables:   {
					filters: {
						notificationIsRead: [{ value: false }],
					},
				},
			};

			const notifsData = cache?.readQuery(unreadNotifsQuery);

			cache?.writeQuery({
				...unreadNotifsQuery,
				data: {
					notifications: {
						items: notifsData?.notifications?.items?.filter(notif => !notif.notificationIsRead),
					},
				},
			});
		},
	});

	const notificationIcon = notification.notificationType === NotificationType.Error
		? (
			<WarningSignOutline
				width={16}
				height={16}
			/>
		)
		: (
			<Airplane
				width={16}
				height={16}
			/>
		);

	const entityAndValidationParams = useMemo(() => getUrlParamsAndValidationsFromNotification(notification), [notification]);

	const navigateToValidationEntity = () => {
		if (entityAndValidationParams.urlParams) {
			onClose();

			setUrlParams({
				...params,
				...entityAndValidationParams.urlParams,
			});
		}

		if (entityAndValidationParams.validations?.length > 0) {
			setValidations(entityAndValidationParams.validations);
		}
	};

	return (
		<div
			className="flex flex-1 w-full pt-[4px] pb-[8px] gap-[8px] justify-between items-center"
		>
			<div className="flex gap-[8px] flex-1">
				<div className="p-[4px]">
					{notificationIcon}
				</div>

				<div
					className={
						classNames(
							'mt-[2px] group text-left',
							{ 'cursor-pointer': !!entityAndValidationParams?.urlParams },
						)
					}
					onClick={navigateToValidationEntity}
				>
					<div
						className={
							classNames(
								'text-black ',
								{ 'group-hover:text-blue-500 cursor-pointer': !!entityAndValidationParams?.urlParams },
							)
						}
					>
						{notification.notificationTitle}
					</div>
					{notification.notificationMessage}
				</div>
			</div>

			<div>
				{!notification.notificationIsRead
				&& (
					<Button
						onClick={async () => {
							await markNotificationsAsRead({
								variables: {
									notificationIds: [notification.notificationId],
								},
							});
						}}
						appearance={ButtonAppearance.GHOST}
						color={ButtonColor.GRAY}
						disabled={loading}
						leftNode={
							loading ? <LoaderBars
								className="animate-spin"
								width={16}
								height={16}
							/> : <CheckIcon
								width={16}
								height={16}
							/>}
					/>
				)
				}
			</div>
		</div>
	);
};

export default NotificationItem;
